import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSeedling,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faDatabase,
    faFileImport
} from '@fortawesome/free-solid-svg-icons';
import { post } from '../../../helpers/apiHelper';

const SystemManagement = () => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSeedTemplates = async () => {
        if (!window.confirm('¿Estás seguro de que deseas ejecutar el seed de templates? Esto eliminará los templates del sistema existentes y creará nuevos.')) {
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await post('infografias/templates/seed');
            setResult(response);
            console.log('Seed ejecutado exitosamente:', response);
        } catch (err) {
            console.error('Error ejecutando seed:', err);
            setError(err.message || 'Error desconocido al ejecutar el seed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                    <FontAwesomeIcon icon={faDatabase} className="text-blue-600 text-2xl" />
                    <h2 className="text-2xl font-bold text-gray-900">Gestión del Sistema</h2>
                </div>
                <p className="text-gray-600">
                    Herramientas de administración para gestionar datos del sistema y configuraciones iniciales.
                </p>
            </div>

            {/* Sección de Seeds */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3 mb-2">
                        <FontAwesomeIcon icon={faSeedling} className="text-green-600 text-xl" />
                        <h3 className="text-xl font-semibold text-gray-900">Seeds de Datos</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                        Ejecuta seeds para inicializar o resetear datos del sistema
                    </p>
                </div>

                <div className="p-6 space-y-6">
                    {/* Templates Seed Card */}
                    <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <FontAwesomeIcon icon={faFileImport} className="text-purple-600" />
                                    <h4 className="text-lg font-semibold text-gray-900">Templates de Infografías</h4>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    Crea los templates base del sistema para infografías:
                                </p>
                                <ul className="text-sm text-gray-600 mb-4 ml-4 list-disc space-y-1">
                                    <li><strong>Cartel de Feedback</strong> - Template para recoger opiniones</li>
                                    <li><strong>Cartel Promocional con QR</strong> - Template para promociones</li>
                                    <li><strong>Cartel de Reseñas Google</strong> - Template para reviews</li>
                                </ul>

                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                                    <div className="flex items-start gap-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
                                        <div className="text-sm text-yellow-800">
                                            <strong>Advertencia:</strong> Esta acción eliminará los templates del sistema existentes antes de crear los nuevos. Los templates personalizados de los usuarios no se verán afectados.
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleSeedTemplates}
                                    disabled={loading}
                                    className={`
                    flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors
                    ${loading
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }
                  `}
                                >
                                    {loading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                            Ejecutando...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSeedling} />
                                            Ejecutar Seed
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Resultado del Seed */}
                        {result && (
                            <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                                <div className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
                                    <div className="flex-1">
                                        <h5 className="font-semibold text-green-900 mb-2">Seed ejecutado exitosamente</h5>
                                        <div className="text-sm text-green-800 space-y-1">
                                            <p className="mb-2">{result.message}</p>
                                            <p><strong>Templates creados:</strong> {result.templatesCreated || 0}</p>
                                            {result.templates && result.templates.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="font-medium mb-2">Detalles de templates:</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        {result.templates.map((template, index) => (
                                                            <li key={template.id || index} className="text-sm">
                                                                <strong>{template.nombre}</strong> - {template.categoria}
                                                                <span className="text-green-700 ml-2">
                                                                    ({template.elementos} elementos, {template.variables} variables)
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                                <div className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                                    <div className="flex-1">
                                        <h5 className="font-semibold text-red-900 mb-1">Error al ejecutar el seed</h5>
                                        <p className="text-sm text-red-800">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Placeholder para futuros seeds */}
                    <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500">
                        <FontAwesomeIcon icon={faDatabase} className="text-3xl mb-2 opacity-50" />
                        <p className="text-sm">Más opciones de seeds estarán disponibles próximamente</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemManagement;

